import type { Faro } from '@grafana/faro-web-sdk';
import pkg from '../../package.json';
import { getConfig } from '../config';

let faroInstance: Faro | undefined;
let initializing = false;

/**
 * Work waiting for Faro to finish loading. The SDK arrives through a dynamic
 * import started from `TelemetryInit`'s effect, so a crash during the first
 * render always happens before `getFaro()` returns anything. Capped so a
 * render loop can't grow it without limit.
 */
const MAX_PENDING_READY_CALLBACKS = 20;
let pendingReadyCallbacks: ((faro: Faro) => void)[] = [];

/**
 * Runs `callback` with Faro now if it is loaded, otherwise as soon as it
 * finishes loading. If the SDK never loads, the callback never runs.
 */
export const whenTelemetryReady = (callback: (faro: Faro) => void): void => {
  if (faroInstance) {
    callback(faroInstance);
    return;
  }
  if (pendingReadyCallbacks.length < MAX_PENDING_READY_CALLBACKS) {
    pendingReadyCallbacks.push(callback);
  }
};

/** Exported so tests can stand in for `initTelemetry` with their own instance. */
export const setFaroInstance = (faro: Faro): void => {
  faroInstance = faro;
  const callbacks = pendingReadyCallbacks;
  pendingReadyCallbacks = [];
  callbacks.forEach((callback) => callback(faro));
};

// Two separate Grafana Cloud Frontend Observability app registrations —
// production ("curi live", appId 1983) and staging ("curi live staging",
// appId 2011) — so the two environments' data/dashboards stay fully
// separate rather than just filterable by `app.environment` within one app.
// Must exactly match the `--app-name` (and `--app-id`) passed to
// `faro-cli inject-bundle-id` / `upload` for the corresponding environment
// in .github/workflows/deploy-{dev,production}.yml. Faro auto-detects the
// build's bundle id by reading a `__faroBundleId_<appName>` global that
// `inject-bundle-id` sets on the built JS files — a mismatch here means
// source maps silently never correlate to errors.
const getFaroAppName = () =>
  getConfig().unleash.environment === 'production'
    ? 'curi live'
    : 'curi live staging';

/**
 * Faro's `url` needs to be absolute, and must always be this app's own
 * origin — the browser never talks to Alloy directly (same reasoning as the
 * Unleash proxy at `/api/ff/proxy`: avoid ad-blockers/content filters, hide
 * the vendor).
 */
const getCollectorProxyUrl = () => `${window.location.origin}/api/tel/proxy`;

/**
 * Known-noisy error patterns that aren't worth spending Alloy's rate budget
 * on (Faro's own `ignoreErrors` — no need to hand-roll a denylist check).
 *
 * Faro tests these against `message + " " + name + " " + stack` concatenated
 * together (see `isErrorIgnored` in `@grafana/faro-core`), not the bare
 * message — so a pattern anchored at both ends (`^...$`) can never match,
 * since there's always trailing ` <name> <stack>` after the message. Anchor
 * at the start only (`^...`) to match precisely on how the value begins
 * without accidentally matching that same text if it ever showed up buried
 * inside a stack trace instead.
 *
 * `Script error.` (a cross-origin script throwing with no message/stack,
 * per browser same-origin policy) is deliberately NOT filtered here despite
 * being noisy — it's also the signature of an extension injecting a
 * `<script>` into the page's main world, and tracking possible
 * extension-caused crashes now matters more than the ingest cost of the
 * noise. See `isLikelyBrowserExtensionCrash` (typings/errors.ts) for the
 * other half of that signal, which a bare `Script error.` can't carry on
 * its own since it has no stack to inspect.
 */
export const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  // socket.io ack timeouts (`.timeout(ms).emit(...)`, gameAPI.ts) and
  // transport-level `socket.on('error', ...)` failures — expected noise on
  // flaky classroom wifi, not application bugs. Connection-quality signal
  // is still tracked separately via `hasPoorInternetConnection` /
  // `TimeToConnection`, so nothing is lost by not shipping these as
  // exceptions.
  /^timeout\b/,
  /^websocket error\b/,
];

/**
 * Search-engine crawlers and other non-human clients hit curi.live URLs too
 * (a join-code link shared publicly — e.g. in a lesson recap or a forum
 * post — gets crawled like any other URL) but never actually play a lesson:
 * they fail the socket auth check and produce a stream of "Unauthorized"
 * exceptions that have nothing to do with a real student. Skipping Faro
 * initialization entirely for these means no ingest budget is spent on this
 * whole class of non-human traffic — not just that one exception type, but
 * every event a page load would otherwise generate (web-vitals, session
 * start, etc.).
 *
 * Broad by design: `bot`/`crawl`/`spider` as substrings catch most named
 * crawlers (bingbot, googlebot, yandexbot, baiduspider, ahrefsbot,
 * semrushbot, mj12bot, petalbot, applebot, duckduckbot, telegrambot, ...)
 * without listing each one. The rest are common crawlers/tools/non-browser
 * HTTP clients whose names don't contain any of those three substrings —
 * none of these would ever be a real student's browser either.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawl|spider|slurp|ia_archiver|facebookexternalhit|whatsapp|headless|lighthouse|pagespeed|python-requests|go-http-client|okhttp|postmanruntime|curl\/|wget\//i;

export const isBotUserAgent = (): boolean =>
  typeof navigator !== 'undefined' &&
  BOT_USER_AGENT_PATTERN.test(navigator.userAgent);

/**
 * Fraction of *sessions* (not events) that send any telemetry at all. The
 * fastest lever to pull (env var + redeploy, no code change) if Alloy ever
 * gets overloaded post-launch.
 */
const getSessionSamplingRate = () => {
  const raw = process.env.NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE;
  const parsed = raw ? Number(raw) : 1;
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
};

/**
 * Flipped by `forceFullSession()` (see useTelemetry.tsx) for a session that
 * must not be lost to `getSessionSamplingRate()`.
 *
 * A plain overwrite of the session's `isSampled` attribute isn't enough:
 * Faro's own SessionInstrumentation reconciles the live session against
 * sessionStorage on every `setSession()` call, and whenever it sees the
 * attributes differ from storage it re-draws `isSampled()` from scratch —
 * silently reverting a one-off attribute poke back to the configured rate's
 * verdict. Routing the override through the `sampler` below means that
 * re-draw also sees the override, instead of undoing it.
 */
let forceFullSampling = false;

export const setForceFullSampling = (): void => {
  forceFullSampling = true;
};

/** Exported so tests can wire the exact same sampler into their own Faro instance. */
export const fullSessionSampler = (): number | undefined =>
  forceFullSampling ? 1 : undefined;

/**
 * Initializes the Faro browser SDK exactly once per page load, replacing
 * the old New Relic Browser agent. Safe to call from multiple components —
 * only the first call does anything.
 *
 * `@grafana/faro-web-sdk` is loaded via dynamic `import()` rather than a
 * static top-level import: every call site that touches telemetry
 * (`useTelemetry.tsx`, and everything that imports it — `responseSync.ts`,
 * `gameAPI.ts`, etc.) would otherwise pull the whole SDK — and, transitively,
 * a `web-vitals` sub-dependency shipped as ESM-only — into every module
 * graph that references them, including Jest's CJS test environment, which
 * can't parse it. A dynamic import only loads it when this function actually
 * runs (i.e. in the browser, from `TelemetryInit`), not merely referenced.
 */
export const initTelemetry = (): void => {
  if (typeof window === 'undefined') return;
  if (isBotUserAgent()) return;
  if (faroInstance || initializing) return;
  initializing = true;

  void import('@grafana/faro-web-sdk')
    .then(({ initializeFaro, getWebInstrumentations }) => {
      const faro = initializeFaro({
        url: getCollectorProxyUrl(),
        app: {
          name: getFaroAppName(),
          // Pinned to the build SHA, not package.json's version — this is
          // what `inject-bundle-id` matches against for source-map
          // correlation (see the comment on getFaroAppName). `release`
          // below carries the human-facing package.json version instead,
          // so an error can be correlated to "which release shipped this"
          // without disturbing that.
          version: process.env.NEXT_PUBLIC_SHA8,
          release: pkg.version,
          environment: getConfig().unleash.environment,
        },
        sessionTracking: {
          samplingRate: getSessionSamplingRate(),
          sampler: fullSessionSampler,
        },
        // Faro's own dedupe + ignore-list — no need to hand-roll either.
        dedupe: true,
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        instrumentations: getWebInstrumentations({
          captureConsole: false,
          // Reports one `faro.performance.resource` event per fetch/XHR
          // call the app makes (join, postResponse/patchResponse, the 5s
          // voting-result poll, ...) — a volume source on top of our own
          // explicit metrics, without adding signal we're currently using.
          // Disabled deliberately; revisit if per-request timing breakdowns
          // become worth the extra events.
          enablePerformanceInstrumentation: false,
        }),
      });
      setFaroInstance(faro);
    })
    .finally(() => {
      initializing = false;
    });
};

export const getFaro = () => faroInstance;
